import zod from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import TextFieldComponent from '../../../components/TextFieldComponent';
import { Grid2 } from '@mui/material';
import ButtonComponent from '../../../components/ButtonComponent';
import {
  useCreateSponteIntegrationAsync,
  useGetSponteIntegrationByIdAsync,
  useUpdateSponteIntegrationAsync,
} from '../../../services/IntegrationService/SponteService/hooks/useMutation';
import CheckboxComponent from '../../../components/CheckboxComponent';
import { useContext, useEffect } from 'react';
import { AppContext } from '../../../context/AppContextProvider';
import { SponteIntegrationFormSchema } from '../../../services/IntegrationService/SponteService/schemas';

interface SponteEditionFormProps {
  sponteIdToUpdate?: number;
  handleSaveForm?: () => void;
}

type SponteEditionFormInput = zod.infer<typeof SponteIntegrationFormSchema>;

const SponteEditionForm = ({
  sponteIdToUpdate,
  handleSaveForm,
}: SponteEditionFormProps) => {
  const { showSnackbar } = useContext(AppContext);
  const { control, handleSubmit, reset } = useForm<SponteEditionFormInput>({
    resolver: zodResolver(SponteIntegrationFormSchema),
    defaultValues: {
      name: '',
      description: '',
      isActive: true,
      codigoCliente: undefined,
      token: '',
    },
  });

  const {
    mutate: mutateCreateSponteIntegration,
    isLoading: isLoadingCreateSponteIntegration,
  } = useCreateSponteIntegrationAsync();
  const {
    mutate: mutateUpdateSponteIntegration,
    isLoading: isLoadingUpdateSponteIntegration,
  } = useUpdateSponteIntegrationAsync();
  const {
    mutateAsync: mutateGetSponteIntegrationByIdAsync,
    isLoading: isLoadingGetSponteIntegrationById,
  } = useGetSponteIntegrationByIdAsync();

  const handleSave = (data: SponteEditionFormInput) => {
    if (sponteIdToUpdate) {
      mutateUpdateSponteIntegration(
        { id: sponteIdToUpdate, data },
        {
          onSuccess: () => {
            saveIntegration('Integração Sponte atualizada com sucesso.');
          },
        },
      );
      return;
    }
    mutateCreateSponteIntegration(data, {
      onSuccess: () => {
        saveIntegration('Integração Sponte criada com sucesso.');
      },
    });
  };

  const saveIntegration = (message: string) => {
    reset();
    showSnackbar(message, 'success');
    if (handleSaveForm) handleSaveForm();
  };

  useEffect(() => {
    if (sponteIdToUpdate) {
      mutateGetSponteIntegrationByIdAsync(sponteIdToUpdate).then((r) =>
        reset(r, { keepDefaultValues: true }),
      );
    }
  }, [sponteIdToUpdate]);

  return (
    <>
      <Grid2 container columnSpacing={2}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label="Nome"
              />
            )}
            name={'name'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label="Código do Cliente"
              />
            )}
            name={'codigoCliente'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label="Token de acesso"
              />
            )}
            name={'token'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label="Descrição"
              />
            )}
            name={'description'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'right' }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <CheckboxComponent
                value={value}
                onChange={onChange}
                label="Ativo"
              />
            )}
            name={'isActive'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ mt: 2 }}>
          <ButtonComponent
            fullWidth
            onClick={handleSubmit(handleSave)}
            loading={
              isLoadingCreateSponteIntegration ||
              isLoadingGetSponteIntegrationById ||
              isLoadingUpdateSponteIntegration
            }
          >
            Salvar
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </>
  );
};

export default SponteEditionForm;
